import { FC, Fragment } from "react";
import { useRouter } from "next/router";
import Link from "next/link";
import IconlyCategoryIcon from "@svg/new-design/bottom-tab-navigation/CategoryIcon";
import IconlyFilledCategoryIcon from "@svg/new-design/bottom-tab-navigation/CategoryIconFilled";
import IconlyFilledBookIcon from "@svg/new-design/bottom-tab-navigation/BookIconFilled";
import IconlyBookIcon from "@svg/new-design/bottom-tab-navigation/BookIcon";
import IconlyHomeIcon from "@svg/new-design/bottom-tab-navigation/HomeIcon";
import IconlyFilledHomeIcon from "@svg/new-design/bottom-tab-navigation/HomeIconFilled";
import IconlyUserIcon from "@svg/new-design/bottom-tab-navigation/UserIcon";
import IconlyUserIconFilled from "@svg/new-design/bottom-tab-navigation/UserIconFilled";
import BTNCategoryButton from "./CategoryButton";
import { useIsMobile } from "@shared/hooks/useIsMobile";
import { lightTheme } from "../../../../../configs/color/colors";
import classNames from "classnames";
import FillShoppingCartIcon from "@svg/new-design/fill-shopping-cart";
import BasketIcon from "@svg/header/buy";
import { toFarsiNumber } from "@utils/digits";
import { useCartContext } from "@context/cart";
import { useLayoutContext } from "@context/layout";
import SubscriptionIconParent from "./SubscriptionIconParent";

const SELECT_COLOR = lightTheme?.fds?.brand?.select || "#224788";

const BottomTabNavigation: FC = () => {
  const { userData, subscriptionData } = useLayoutContext();
  const isMobile = useIsMobile(768);
  const { cart } = useCartContext();
  const { pathname } = useRouter();

  const hasActiveSubscription = Boolean(subscriptionData?.is_active);

  const navigationItems = [
    {
      title: "خانه",
      path: "/",
      icon: <IconlyHomeIcon className="h-5 w-5" />,
      filledIcon: (
        <IconlyFilledHomeIcon className="h-5 w-5" fill={SELECT_COLOR} />
      ),
    },
    {
      title: "دسته‌بندی‌",
      path: "/how-to-learn/everything",
      icon: <IconlyCategoryIcon className="h-5 w-5" />,
      filledIcon: (
        <IconlyFilledCategoryIcon className="h-5 w-5" fill={SELECT_COLOR} />
      ),
      buttonComponent: <BTNCategoryButton />,
    },
    {
      title: "سبد خرید",
      path: userData?.is_login ? "/checkout" : "/cart",
      icon: <BasketIcon className="h-5 w-5" />,
      filledIcon: (
        <FillShoppingCartIcon className="h-5 w-5" fill={SELECT_COLOR} />
      ),
      badge: cart.length ? (
        <div className="absolute rounded-full ml-3.5 top-5 h-3.5 w-3.5 bg-fds-brand flex items-center justify-center">
          <span className="text-fds-on-brand body-xsm pt-fds-1">
            {toFarsiNumber(cart.length)}
          </span>
        </div>
      ) : null,
      hidden: hasActiveSubscription,
    },
    {
      title: "آموزش‌های من",
      path: "/my-account/courses",
      url: "/my-account/courses",
      icon: <IconlyBookIcon className="h-5 w-5" />,
      filledIcon: (
        <IconlyFilledBookIcon className="h-5 w-5" fill={SELECT_COLOR} />
      ),
    },
    {
      title: "پروفایل",
      path: "/my-account",
      icon: hasActiveSubscription ? (
        <SubscriptionIconParent>
          <IconlyUserIcon className="h-5 w-5" />
        </SubscriptionIconParent>
      ) : (
        <IconlyUserIcon className="h-5 w-5" />
      ),
      filledIcon: hasActiveSubscription ? (
        <SubscriptionIconParent>
          <IconlyUserIconFilled className="h-5 w-5" fill={SELECT_COLOR} />
        </SubscriptionIconParent>
      ) : (
        <IconlyUserIconFilled className="h-5 w-5" fill={SELECT_COLOR} />
      ),
    },
  ];
  if (!isMobile) return null;

  return (
    <>
      <nav
        className="fixed bottom-0 left-0 right-0 h-16 bg-fds-on-inverse flex items-center justify-around z-layout md:hidden shadow-bottom-tab-navigation"
        id="bottom-tab-navigation"
      >
        {navigationItems.map((navI, i) => {
          if (navI.hidden === true) return null;
          const isCoursePage = pathname === "/my-account/courses";
          const isProfilePage = navI.path === "/my-account";
          const isActive =
            pathname.includes("/my-account") && !isCoursePage && isProfilePage
              ? pathname.includes(navI.path)
              : pathname === navI.path;
          return (
            <Fragment key={i}>
              {navI.buttonComponent || (
                <Link
                  href={navI.path}
                  className="grow w-full h-full flex flex-col items-center justify-center gap-1"
                >
                  {isActive ? navI.filledIcon : navI.icon}
                  {navI.badge}
                  <span
                    className={classNames(
                      "body-xsm leading-5 text-center ",
                      isActive ? "text-fds-brand-select" : "",
                      i == 0 ? "title-sm" : ""
                    )}
                  >
                    {navI.title}
                  </span>
                </Link>
              )}
            </Fragment>
          );
        })}
      </nav>
    </>
  );
};

export default BottomTabNavigation;
